import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import { contabilizacionApi } from "../services/contabilizacion.api";

/**
 * Descarga el TXT de una póliza individual. No tiene toast de éxito ·
 * el archivo aparece en la barra del browser, eso es feedback suficiente.
 */
export function useDescargarPolizaTxt(solicitudId: string) {
  return useMutation({
    mutationFn: ({ polizaId, nombre }: { polizaId: string; nombre: string }) =>
      contabilizacionApi.descargarPolizaTxt(solicitudId, polizaId, nombre),
    onError: toastErrorApi,
  });
}

/**
 * Descarga TODAS las pólizas depositadas como un ZIP. Acá sí toast,
 * porque el archivo es uno solo con un nombre derivado que el usuario
 * podría no esperar.
 */
export function useDescargarPolizasZip(solicitudId: string) {
  return useMutation({
    mutationFn: () => contabilizacionApi.descargarPolizasZip(solicitudId),
    onSuccess: () => toastExito({ title: "ZIP descargado", message: "Pólizas descargadas correctamente." }),
    onError: toastErrorApi,
  });
}

/**
 * Hotspot 53 · «Reintentar depósito» de una póliza en error de depósito. Refresca la lista
 * de pólizas en éxito y en error (un reintento fallido también cambia el estado de la póliza).
 */
export function useReintentarDepositoPoliza(solicitudId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (polizaId: string) => contabilizacionApi.reintentarDeposito(solicitudId, polizaId),
    onSuccess: () => toastExito({ title: "Póliza depositada", message: "El depósito se reintentó correctamente." }),
    onError: toastErrorApi,
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.polizas(solicitudId) }),
  });
}

/**
 * Hotspots 53/109 · «Regenerar póliza»: la vuelve a armar con los datos actuales, la revalida
 * ante el SAT y la deposita. Refresca la lista en éxito y en error (la póliza puede cambiar).
 */
export function useRegenerarPoliza(solicitudId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (polizaId: string) => contabilizacionApi.regenerar(solicitudId, polizaId),
    onSuccess: () => toastExito({ title: "Póliza regenerada", message: "La póliza se volvió a generar y se depositó." }),
    onError: toastErrorApi,
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.polizas(solicitudId) }),
  });
}
